import { useMemo } from "react";
import { useAppSelector } from "@/app/store";
import { getThemeConfig, readStoredBrandColors } from "@/config/themes";
import { type BrandColors, toBrandColors } from "@/config/themes/brandColors";
import type { TenantThemeConfig } from "@/config/themes/types";
import { STATUS } from "@/constants";
import { useResolvedThemeMode } from "@/features/appearance/hooks/useResolvedThemeMode";

/**
 * The tenant's brand colours: the loaded branding once it has arrived, else the last-known ones
 * from storage. The fallback is what keeps a branded tenant from flashing Olive's green before
 * the branding request returns — and on the sign-in page, where it is never made.
 */
export function useBrandColors(): BrandColors | null {
  const status = useAppSelector((state) => state.branding.status);
  const primary = useAppSelector((state) => state.branding.branding.primary_color);
  const secondary = useAppSelector((state) => state.branding.branding.secondary_color);

  return useMemo(
    () => (status === STATUS.SUCCEEDED ? toBrandColors(primary, secondary) : readStoredBrandColors()),
    [status, primary, secondary],
  );
}

/** The theme in effect: the appearance mode, with the tenant's brand colours applied if it has any. */
export function useResolvedThemeConfig(): TenantThemeConfig {
  const mode = useResolvedThemeMode();
  const brand = useBrandColors();
  return useMemo(() => getThemeConfig(mode, brand), [mode, brand]);
}
